'use client';

// `use client` is needed to serialize the custom loader function

import { UploadcareImage } from '@prezly/uploadcare';
import classNames from 'classnames';
import NextImage from 'next/image';
import type { ComponentProps, SyntheticEvent } from 'react';
import { useState } from 'react';

import { createUploadcareImageLoader, getImageDetails } from './lib';
import type { ImageEffects, ImageSource } from './types';

import styles from './Image.module.scss';

export function Image({
    src: imageSource,
    effects,
    withFadeInAnimation = true,
    fill,
    className,
    onLoad,
    ...props
}: Image.Props) {
    const [isLoaded, setIsLoaded] = useState(false);
    const { src, width = 400, height = 400 } = getImageDetails(imageSource, effects);

    const classes = classNames(styles.image, className, {
        [styles.withFadeInAnimation]: withFadeInAnimation,
        [styles.isLoaded]: isLoaded,
    });

    function handleOnLoad(event: SyntheticEvent<HTMLImageElement>): void {
        setIsLoaded(true);
        onLoad?.(event);
    }

    if (UploadcareImage.isPrezlyStoragePayload(imageSource)) {
        const loader = createUploadcareImageLoader(imageSource, effects);

        return (
            <NextImage
                className={classes}
                src={src}
                width={!fill ? width : undefined}
                height={!fill ? height : undefined}
                fill={fill}
                loader={loader}
                onLoad={handleOnLoad}
                {...props}
            />
        );
    }

    return (
        <NextImage
            className={classes}
            src={src}
            width={!fill ? width : undefined}
            height={!fill ? height : undefined}
            fill={fill}
            onLoad={handleOnLoad}
            {...props}
        />
    );
}

export namespace Image {
    export interface Props
        extends Omit<ComponentProps<typeof NextImage>, 'src' | 'width' | 'height'> {
        src: ImageSource;
        withFadeInAnimation?: boolean;
        effects?: ImageEffects;
    }
}
